import type { SourceDef } from './types';
import { probeUrl } from './layers';

/**
 * Archive epochs cover only part of the country each year — ČÚZK photographed a
 * third of it per year until 2013 and a half since, and Prague's older years are
 * split north/south. Rather than let the user scrub through blank years, we ask
 * the service for an 8×8 pixel image over the current spot and look at whether
 * anything came back. Both services allow cross-origin reads, so the alpha
 * channel is readable; a failure or timeout leaves the epoch 'unknown', never
 * wrongly dimmed.
 */
export type Coverage = 'covered' | 'empty' | 'unknown';

const CELL = 2000; // metres; one probe result stands for this much of the map
const MAX_CONCURRENT = 3;
const TIMEOUT_MS = 7000;

const cache = new Map<string, Coverage>();
const inflight = new Map<string, Promise<Coverage>>();
let running = 0;
const queue: (() => void)[] = [];

function cellKey(def: SourceDef, timeId: string, center: [number, number]): string {
  const cx = Math.round(center[0] / CELL);
  const cy = Math.round(center[1] / CELL);
  return `${def.id}|${timeId}|${cx}|${cy}`;
}

function slot<T>(run: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = () => {
      running++;
      run()
        .then(resolve, reject)
        .finally(() => {
          running--;
          queue.shift()?.();
        });
    };
    if (running < MAX_CONCURRENT) start();
    else queue.push(start);
  });
}

async function readAlpha(url: string): Promise<Coverage> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: ctrl.signal, mode: 'cors' });
    if (!res.ok) return 'unknown';
    const blob = await res.blob();
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return 'unknown';
    ctx.drawImage(bitmap, 0, 0);
    const { data } = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    bitmap.close();
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 16) return 'covered';
    }
    return 'empty';
  } catch {
    return 'unknown';
  } finally {
    clearTimeout(timer);
  }
}

export function cachedCoverage(def: SourceDef, timeId: string, center: [number, number]): Coverage {
  return cache.get(cellKey(def, timeId, center)) ?? 'unknown';
}

export function probeCoverage(def: SourceDef, timeId: string, center: [number, number]): Promise<Coverage> {
  const key = cellKey(def, timeId, center);
  const known = cache.get(key);
  if (known) return Promise.resolve(known);
  const pending = inflight.get(key);
  if (pending) return pending;

  const half = CELL / 20; // a 200 m box around the centre
  const bbox: [number, number, number, number] = [
    center[0] - half,
    center[1] - half,
    center[0] + half,
    center[1] + half,
  ];
  const url = probeUrl(def, timeId, bbox);
  if (!url) return Promise.resolve('unknown');

  const p = slot(() => readAlpha(url)).then((result) => {
    if (result !== 'unknown') cache.set(key, result);
    inflight.delete(key);
    return result;
  });
  inflight.set(key, p);
  return p;
}

/** Probe a whole series, reporting each answer as it lands. */
export function probeSeries(
  def: SourceDef,
  timeIds: string[],
  center: [number, number],
  onResult: (timeId: string, coverage: Coverage) => void,
): void {
  for (const timeId of timeIds) {
    const known = cache.get(cellKey(def, timeId, center));
    if (known) {
      onResult(timeId, known);
      continue;
    }
    void probeCoverage(def, timeId, center).then((c) => onResult(timeId, c));
  }
}
